import { ExternalLink, FileText, LayoutGrid, Link as LinkIcon, MessagesSquare, Paperclip, Trash2 } from "lucide-react";
import { emailPlainText, readableQuote, splitQuoted } from "../../emailText";
import type { TicketDetail } from "../../types";
import { Fragment, useState } from "react";
import { dateTime, lineTotal, money } from "../../format";
import { totals } from "../../pricing";
import { Card, Later, Toggle } from "./Card";
import { useDraft } from "./estimateDraft";
import { Modal } from "./Modal";
import { useAttachmentOpener } from "./attachments";
import { useTicketContext } from "./useTicket";

export function EstimateCard() {
  const { detail, lookups, save, saving } = useTicketContext();
  const { draft, setDraft, dirty, saveDraft, discard } = useDraft();
  const [dialog, setDialog] = useState<"publish" | "invoice" | null>(null);
  const t = detail.ticket;
  const locked = t.servicesLocked;
  const taxed = !!detail.customer?.chargeSalesTax && (t.state ?? "").trim().toUpperCase() === "WI";
  const tot = lookups ? totals(draft.lines, Number(draft.insurance || 0), taxed, lookups.pricing) : null;
  // Lines grouped by watch, in watch order; "Whole ticket" (incl. return shipping) last.
  const groups = [...detail.watches.map((w) => ({ key: w.id, label: `Watch ${w.position}${w.brand ? ` · ${[w.brand, w.model].filter(Boolean).join(" ")}` : ""}` })),
    { key: "none", label: "Whole ticket" }]
    .map((g) => ({ ...g, rows: draft.lines.map((l, i) => ({ l, i })).filter(({ l }) => (l.watchId ?? "none") === g.key) }))
    .filter((g) => g.rows.length);
  const setLine = (i: number, patch: Partial<(typeof draft.lines)[number]>) =>
    setDraft({ ...draft, lines: draft.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });

  return (
    <Card title="Estimate" icon={FileText} className={dirty ? "dirty" : ""}>
      <textarea className="est-msg" rows={2} value={draft.message} aria-label="Message to the customer with the estimate"
        placeholder="Message to the customer with the estimate…" onChange={(e) => setDraft({ ...draft, message: e.target.value })} />
      <table className="est">
        <thead><tr><th>Service</th><th className="center">Qty</th><th className="num">Price</th><th className="num">Discount</th><th className="num">Subtotal</th><th aria-label="Remove" /></tr></thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.key}>
              {groups.length > 1 && <tr className="group-row"><td colSpan={6}>{g.label}</td></tr>}
              {g.rows.map(({ l, i }) => (
            <tr key={i}>
              <td>
                <input className="flat strong" value={l.name} disabled={locked} aria-label="Service" onChange={(e) => setLine(i, { name: e.target.value })} />
                {(l.description || !locked) && <input className={`flat desc${l.description ? "" : " empty"}`} value={l.description ?? ""} disabled={locked} placeholder="Add description"
                  aria-label="Description" onChange={(e) => setLine(i, { description: e.target.value || null })} />}
              </td>
              <td className="center"><input className="flat qty" type="number" min={1} value={l.quantity} disabled={locked} aria-label="Quantity"
                onChange={(e) => setLine(i, { quantity: Math.max(1, Number(e.target.value) || 1) })} /></td>
              <td className="num"><input className="flat money" type="number" min={0} step="0.01" value={l.price} disabled={locked} aria-label="Price"
                onChange={(e) => setLine(i, { price: e.target.value })} /></td>
              <td className="num"><input className="flat money" type="number" min={0} step="0.01" value={l.discount ?? ""} placeholder="-" disabled={locked}
                aria-label="Discount per item" onChange={(e) => setLine(i, { discount: e.target.value || null })} /></td>
              <td className="num">{money(lineTotal(l))}</td>
              <td><button type="button" className="icon-link danger" disabled={locked} aria-label="Remove line"
                onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })}><Trash2 size={15} /></button></td>
            </tr>
              ))}
            </Fragment>
          ))}
          {!draft.lines.length && <tr><td colSpan={6} className="hint">Tick services on the left to build the estimate.</td></tr>}
        </tbody>
      </table>
      {!locked && <button type="button" className="link-btn" onClick={() => setDraft({ ...draft, lines: [...draft.lines,
        { serviceId: null, name: "", description: null, quantity: 1, price: "0", discount: null }] })}>+ Custom line</button>}
      <div className="ins-row">
        <label>Insurance Amount ($)
          <input type="number" min={0} step="1" value={draft.insurance} placeholder="0" disabled={locked}
            onChange={(e) => setDraft({ ...draft, insurance: e.target.value })} /></label>
        <span className="hint">{tot && tot.insuranceCost > 0 ? `Insurance cost ${money(tot.insuranceCost)}` : "First $100 free, then 3%"}</span>
        {taxed && tot && <span className="hint">WI tax {money(tot.tax)}</span>}
      </div>
      <div className="est-total"><span>Total Invoice Amount</span><strong>{money(tot?.total ?? 0)}</strong></div>
      {dirty && (
        <div className="save-bar" role="status">
          <span>Unsaved estimate changes</span>
          <button type="button" className="btn primary" disabled={saving} onClick={() => void saveDraft()}>{saving ? "Saving…" : "Save changes"}</button>
          <button type="button" className="btn outline" onClick={discard}>Discard</button>
        </div>
      )}
      <div className="btn-row even three">
        <button type="button" className="btn primary" disabled={dirty || saving || !detail.items.length}
          title={dirty ? "Save the estimate changes first" : !detail.items.length ? "Add services first" : "Send the estimate to the customer"}
          onClick={() => setDialog("publish")}>Publish Estimate</button>
        <button type="button" className="btn outline" disabled={dirty || saving || locked || !detail.items.some((i) => !/^return shipping/i.test(i.name))}
          title="Saves these services in Published Estimates and clears them (return shipping stays)"
          onClick={() => { if (window.confirm("Archive this estimate? The services are saved below and cleared from the ticket.")) void save("POST", "/archive-estimate"); }}>
          Archive Estimate</button>
        <button type="button" className="btn green" disabled={dirty || saving || !detail.items.length}
          title={dirty ? "Save the estimate changes first" : "Invoice the customer"} onClick={() => setDialog("invoice")}>Convert to Invoice</button>
      </div>
      {dialog === "publish" && <PublishDialog onClose={() => setDialog(null)} />}
      {dialog === "invoice" && <InvoiceDialog total={tot?.total ?? 0} onClose={() => setDialog(null)} />}
    </Card>
  );
}

const LEAD_WEEKS = [1, 2, 4, 6, 8, 10, 12];
const TEST_NOTE = "Until go-live, customer emails go to the test inbox (joe@) instead.";

/** Who gets the email, and whether automatic email is off for this customer (wholesale, email off). */
function useRecipient() {
  const { detail } = useTicketContext();
  const c = detail.customer;
  return { email: c?.email ?? "", emailOff: !!c?.isWholesale && !c.allowEmail, ...textability(c) };
}

/** Whether the customer can be texted (texts allowed, mobile number), and why not. */
export function textability(c: TicketDetail["customer"]): { canText: boolean; whyNoText: string } {
  const digits = (c?.phone ?? "").replace(/\D/g, "");
  if (!c?.allowText) return { canText: false, whyNoText: "Texts are off for this customer" };
  if (!c.phoneIsMobile) return { canText: false, whyNoText: "The customer's phone is not marked as a mobile" };
  if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith("1")))) return { canText: false, whyNoText: "No valid US mobile number" };
  return { canText: true, whyNoText: "" };
}
const TEXT_NOTE = "Texts written 8 PM to 8 AM wait until 8 AM. Until go-live, texts use Twilio's test mode and are not delivered.";

/** Publish Estimate window (owner, 2026-11-03): lead time, Send email, Send text. */
function PublishDialog({ onClose }: { onClose: () => void }) {
  const { detail, save, saving } = useTicketContext();
  const { email, emailOff, canText, whyNoText } = useRecipient();
  const [weeks, setWeeks] = useState(6);
  const [sendEmail, setSendEmail] = useState(!emailOff);
  const [sendText, setSendText] = useState(canText);
  return (
    <Modal title="Publish Estimate" onClose={onClose}>
      <form className="mini-form" onSubmit={async (e) => {
        e.preventDefault();
        if (await save("POST", "/publish-estimate", { leadTimeWeeks: weeks, sendEmail, sendText, expectedUpdatedAt: detail.ticket.updatedAt })) onClose();
      }}>
        <label>Lead time
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} autoFocus>
            {LEAD_WEEKS.map((w) => <option key={w} value={w}>{w} {w === 1 ? "week" : "weeks"}</option>)}
          </select>
        </label>
        <div className="toggles">
          <Toggle label="Send email" checked={sendEmail} disabled={emailOff} onChange={setSendEmail} />
          <Toggle label="Send text" checked={sendText} disabled={!canText} onChange={setSendText} hint={whyNoText || TEXT_NOTE} />
        </div>
        {emailOff ? <p className="warn">This wholesale customer has email turned off, so they will not be emailed.</p>
          : sendEmail ? <p className="recipient">Email to {email}. {TEST_NOTE}</p> : !sendText ? <p className="recipient">Publishes without contacting the customer.</p> : null}
        {canText ? sendText && <p className="recipient">Text to {detail.customer?.phone}. {TEXT_NOTE}</p> : <p className="recipient">No text: {whyNoText.toLowerCase()}.</p>}
        <p className="hint">Saves a copy in Published Estimates, locks the services and sets the status to Estimate Sent to Customer.</p>
        <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>{saving ? "Publishing…" : "Yes, publish"}</button>
          <button type="button" className="btn outline" onClick={onClose}>No</button></div>
      </form>
    </Modal>
  );
}

/** Convert to Invoice window: invoice message, total, Send email, Send text. */
function InvoiceDialog({ total, onClose }: { total: number; onClose: () => void }) {
  const { detail, save, saving } = useTicketContext();
  const { email, emailOff, canText, whyNoText } = useRecipient();
  const [message, setMessage] = useState(detail.ticket.invoiceMessage ?? "");
  const [sendEmail, setSendEmail] = useState(!emailOff);
  const [sendText, setSendText] = useState(canText);
  const blocked = detail.cardOnFile;
  return (
    <Modal title="Convert to Invoice" onClose={onClose}>
      <form className="mini-form" onSubmit={async (e) => {
        e.preventDefault();
        if (await save("POST", "/convert-to-invoice", { invoiceMessage: message, sendEmail, sendText, expectedUpdatedAt: detail.ticket.updatedAt })) onClose();
      }}>
        <label>Invoice message (optional)
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} autoFocus /></label>
        <p><strong>Total {money(total)}</strong></p>
        <div className="toggles">
          <Toggle label="Send email" checked={sendEmail} disabled={emailOff || blocked} onChange={setSendEmail} />
          <Toggle label="Send text" checked={sendText} disabled={!canText || blocked} onChange={setSendText} hint={whyNoText || TEXT_NOTE} />
        </div>
        {blocked ? <p className="warn">This ticket has a card on file. Charging it comes with the Authorize.Net connection, so it cannot be invoiced here yet.</p>
          : emailOff ? <p className="warn">This wholesale customer has email turned off, so they will not be emailed.</p>
          : sendEmail ? <p className="recipient">Invoice with a pay link to {email}. {TEST_NOTE}</p> : !sendText ? <p className="recipient">Invoices without contacting the customer.</p> : null}
        {!blocked && (canText ? sendText && <p className="recipient">Text with the pay link to {detail.customer?.phone}. {TEXT_NOTE}</p> : <p className="recipient">No text: {whyNoText.toLowerCase()}.</p>)}
        <p className="hint">Sets the status to Invoiced and the due date 30 days out.</p>
        <div className="btn-row"><button type="submit" className="btn green" disabled={saving || blocked}>{saving ? "Invoicing…" : "Convert to Invoice"}</button>
          <button type="button" className="btn outline" onClick={onClose}>Cancel</button></div>
      </form>
    </Modal>
  );
}

export function PublishedEstimatesCard() {
  const { detail, save, saving } = useTicketContext();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card title="Published Estimates" icon={LayoutGrid}>
      <table className="t-mini">
        <thead><tr><th>Date Saved</th><th className="num">Estimate Total</th><th className="center">Items</th><th className="center">Action</th></tr></thead>
        <tbody>
          {detail.estimateVersions.map((v) => (
            <Fragment key={v.id}>
              <tr>
                <td>{dateTime(v.createdAt)}{v.reason === "archived" && <span className="tag">archived</span>}</td>
                <td className="num">{money(v.items.reduce((s, i) => s + lineTotal(i), 0))}</td>
                <td className="center"><button type="button" className="link-btn" aria-expanded={open === v.id} onClick={() => setOpen(open === v.id ? null : v.id)}>{v.items.length}</button></td>
                <td className="center nowrap">
                  <button type="button" className="link-btn" disabled={saving || detail.ticket.servicesLocked}
                    onClick={() => { if (window.confirm(`Replace the current services with this estimate?`)) void save("POST", "/restore-estimate", { versionId: v.id }); }}>Restore</button>
                  <button type="button" className="icon-link danger" aria-label="Delete estimate" disabled={saving}
                    onClick={() => { if (window.confirm("Delete this saved estimate? This cannot be undone.")) void save("DELETE", `/estimate-versions/${v.id}`); }}><Trash2 size={16} /></button>
                </td>
              </tr>
              {open === v.id && <tr className="sub-row"><td colSpan={4}>{v.items.map((i, n) => <div key={n}>{i.quantity} × {i.name} · {money(lineTotal(i))}</div>)}</td></tr>}
            </Fragment>
          ))}
          {!detail.estimateVersions.length && <tr><td colSpan={4} className="hint">None yet.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

export function LinksCard() {
  return (
    <Card title="Links" icon={LinkIcon}>
      <div className="btn-row even">
        <Later icon={ExternalLink} why="the customer order page (pay.watchrepairpros.com)">Order Detail</Later>
        <Later icon={LayoutGrid} why="the customer payment page (pay.watchrepairpros.com)">Payment Page</Later>
      </div>
    </Card>
  );
}

type Message = TicketDetail["messages"][number];

const fileSize = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** The customer's words first; quoted earlier emails folded away; attachments as download links. */
function MessageBody({ m }: { m: Message }) {
  const [open, setOpen] = useState(false);
  const { open: download, failed } = useAttachmentOpener();
  const text = emailPlainText(m.body);
  const { main, earlier } = m.isEmail ? splitQuoted(text) : { main: text, earlier: "" };
  return (
    <>
      <div className="body">{main}</div>
      {earlier && (
        <button type="button" className="link quoted-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? "Hide earlier messages" : "Show earlier messages"}
        </button>
      )}
      {open && <div className="body quoted">{readableQuote(earlier)}</div>}
      {m.attachments.length > 0 && (
        <ul className="attachments">
          {m.attachments.map((a) => (
            <li key={a.id}>
              <button type="button" className="link" onClick={() => download(a.id)} title="Download">
                <Paperclip size={14} aria-hidden /> {a.filename} <span className="hint">({fileSize(a.size)})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {failed && <div className="hint error">Could not open the attachment: {failed}</div>}
    </>
  );
}

export function MessagesCard() {
  const { detail, save, saving } = useTicketContext();
  const [draftMsg, setDraftMsg] = useState("");
  const t = detail.ticket;
  const unread = detail.messages.filter((m) => m.unread).length + detail.texts.filter((m) => m.unread).length;
  const name = [detail.customer?.firstName, detail.customer?.lastName].filter(Boolean).join(" ") || "Customer";
  const patch = (body: Record<string, unknown>) => void save("PATCH", "", { ...body, expectedUpdatedAt: t.updatedAt });
  return (
    <Card title="Messages" icon={MessagesSquare} badge={unread > 0 ? <span className="count-badge">{unread} New</span> : undefined}>
      <form className="compose" onSubmit={async (e) => {
        e.preventDefault();
        if (await save("POST", "/messages", { body: draftMsg })) setDraftMsg("");
      }}>
        <textarea rows={3} placeholder="Enter a message to the customer..." aria-label="New message" value={draftMsg}
          onChange={(e) => setDraftMsg(e.target.value)} maxLength={5000} />
        <button type="submit" className="btn primary send" disabled={saving || !draftMsg.trim()}
          title={`Emails ${detail.customer?.email ?? "the customer"}. ${TEST_NOTE}`}>{saving ? "Sending…" : "Send"}</button>
      </form>
      <ul className="thread">
        {detail.messages.map((m) => (
          <li key={m.id} className={`${m.fromCustomer ? "in" : "out"}${m.unread ? " unread" : ""}`}>
            <div className="meta"><span className="who">{m.fromCustomer ? name : m.isSystem ? "WRP (System)" : "WRP"}</span><span>•</span>
              <span>{dateTime(m.sentAt)}</span>{m.isEmail && <span className="hint">· email</span>}
              {m.fromAddress && m.fromAddress !== detail.customer?.email?.toLowerCase() && <span className="hint">· from {m.fromAddress}</span>}</div>
            <MessageBody m={m} />
          </li>
        ))}
        {!detail.messages.length && <li className="hint plain">No messages yet.</li>}
      </ul>
      <div className="btn-col narrow">
        <button type="button" className="btn outline" disabled={!unread || saving} onClick={() => void save("POST", "/mark-read")}>Mark Ticket Read</button>
      </div>
      <div className="toggles-row">
        <Toggle label="Silence Reminders" checked={t.ignoreReminders} disabled={saving} onChange={(v) => patch({ ignoreReminders: v })}
          hint="Stops the every-2-days estimate and invoice reminders" />
        <Toggle label="Ignore Review Email" checked={!t.includeReviewRequest} disabled={saving} onChange={(v) => patch({ includeReviewRequest: !v })} />
      </div>
    </Card>
  );
}
